import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminUsuarios from './pages/AdminUsuarios';
import AuditLog from './pages/AuditLog';
import Scheduler from './pages/Scheduler';
import Clientes from './pages/Clientes';
import ClienteDetalle from './pages/ClienteDetalle';
import Grupos from './pages/Grupos';
import Zonas from './pages/Zonas';
import PerplexityJob from './pages/PerplexityJob';
import Catalogos from './pages/Catalogos';
import ContactosGlobal from './pages/ContactosGlobal';
import ContratosGlobal from './pages/ContratosGlobal';
import Tareas from './pages/Tareas';
import Productos from './pages/Productos';
import Articulos from './pages/Articulos';
import ImportacionERP from './pages/ImportacionERP';
import Informes from './pages/Informes';
import Homologaciones from './pages/Homologaciones';
import Proyectos from './pages/Proyectos';
import CamposPersonalizados from './pages/CamposPersonalizados';
import AdminPermisos from './pages/AdminPermisos';
import AdminFeatureFlags from './pages/AdminFeatureFlags';
import IntegracionesERP from './pages/IntegracionesERP';
import DocumentosComerciales from './pages/DocumentosComerciales';
// import Depositos from './pages/Depositos';  // desconectado — datos AMD incompletos
import Tarifas from './pages/Tarifas';
import BotsIA from './pages/BotsIA';
import DashboardConfig from './pages/DashboardConfig';
import ProspeccionLayout from './pages/ProspeccionLayout';
import HistoricoPlace from './pages/HistoricoPlace';
import RastreoActual from './pages/RastreoActual';
import ProspeccionGestoras from './pages/ProspeccionGestoras';
import ProspeccionRevision from './pages/ProspeccionRevision';
import PlanSemanal from './pages/PlanSemanal';
import VisitasGlobal from './pages/VisitasGlobal';
import Pedidos from './pages/Pedidos';
import DedupeGestoras from './pages/DedupeGestoras';
import IngestaJobs from './pages/IngestaJobs';
import PedidoDetalle from './pages/PedidoDetalle';
import ConfiguracionAPIs from './pages/ConfiguracionAPIs';
import MapaComercial360 from './pages/MapaComercial360';
import Perfil from './pages/Perfil';
import Correos from './pages/Correos';
import CorreosEnviados from './pages/CorreosEnviados';
import CorreosIngesta from './pages/CorreosIngesta';
import IAStats from './pages/IAStats';
import EnriquecimientoCorreo from './pages/EnriquecimientoCorreo';
import NoticiasSector from './pages/NoticiasSector';
import SectorAgua from './pages/SectorAgua';
import AppShell from './components/AppShell';
import ProtectedRoute from './components/ProtectedRoute';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-brand-panel">
    <div className="text-center animate-fade-in">
      <img src="/assets/neural-network.png" alt="Inside CRM" className="h-16 w-auto mx-auto mb-5 animate-pulse-soft" />
      <p className="text-ink-300 text-xs tracking-[0.25em] uppercase">Cargando Inside CRM…</p>
    </div>
  </div>
);

const Root = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/clientes/:id" element={<ClienteDetalle />} />
        <Route path="/contactos" element={<ContactosGlobal />} />
        <Route path="/contratos" element={<ContratosGlobal />} />
        <Route path="/visitas" element={<VisitasGlobal />} />
        <Route path="/tareas" element={<Tareas />} />
        <Route path="/productos" element={<Productos />} />
        <Route path="/articulos" element={<Articulos />} />
        <Route path="/homologaciones" element={<Homologaciones />} />
        <Route path="/proyectos" element={<Proyectos />} />
        <Route path="/grupos" element={<ProtectedRoute adminOnly><Grupos /></ProtectedRoute>} />
        <Route path="/zonas" element={<ProtectedRoute superAdminOnly><Zonas /></ProtectedRoute>} />
        <Route path="/perplexity/jobs/:jobId" element={<ProtectedRoute adminOnly><PerplexityJob /></ProtectedRoute>} />
        <Route path="/catalogos" element={<ProtectedRoute superAdminOnly><Catalogos /></ProtectedRoute>} />
        <Route path="/organigrama" element={<Navigate to="/clientes" replace />} />
        <Route path="/campos-personalizados" element={<ProtectedRoute superAdminOnly><CamposPersonalizados /></ProtectedRoute>} />
        <Route path="/usuarios" element={<ProtectedRoute superAdminOnly><AdminUsuarios /></ProtectedRoute>} />
        <Route path="/auditoria" element={<ProtectedRoute superAdminOnly><AuditLog /></ProtectedRoute>} />
        <Route path="/scheduler" element={<ProtectedRoute superAdminOnly><Scheduler /></ProtectedRoute>} />
        <Route path="/permisos" element={<ProtectedRoute superAdminOnly><AdminPermisos /></ProtectedRoute>} />
        <Route path="/admin/feature-flags" element={<ProtectedRoute superAdminOnly><AdminFeatureFlags /></ProtectedRoute>} />
        <Route path="/integraciones-erp" element={<ProtectedRoute adminOnly><IntegracionesERP /></ProtectedRoute>} />
        <Route path="/erp/importar" element={<ProtectedRoute adminOnly><ImportacionERP /></ProtectedRoute>} />
        <Route path="/documentos-comerciales" element={<ProtectedRoute><DocumentosComerciales /></ProtectedRoute>} />
        <Route path="/informes" element={<ProtectedRoute permiso={{ recurso: 'informes', accion: 'leer' }}><Informes /></ProtectedRoute>} />
        {/* <Route path="/depositos" element={<ProtectedRoute><Depositos /></ProtectedRoute>} /> */}{/* desconectado */}
        <Route path="/tarifas" element={<ProtectedRoute><Tarifas /></ProtectedRoute>} />
        <Route path="/bots-ia" element={<ProtectedRoute adminOnly><BotsIA /></ProtectedRoute>} />
        <Route path="/dashboard-config" element={<ProtectedRoute superAdminOnly><DashboardConfig /></ProtectedRoute>} />
        <Route path="/prospeccion" element={<ProtectedRoute adminOnly><ProspeccionLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/prospeccion/historico" replace />} />
          <Route path="historico" element={<HistoricoPlace />} />
          <Route path="rastreo" element={<RastreoActual />} />
          <Route path="gestoras" element={<ProspeccionGestoras />} />
          <Route path="revision" element={<ProspeccionRevision />} />
        </Route>
        <Route path="/plan-semanal" element={<ProtectedRoute><PlanSemanal /></ProtectedRoute>} />
        <Route path="/pedidos" element={<ProtectedRoute><Pedidos /></ProtectedRoute>} />
        <Route path="/pedidos/:id" element={<ProtectedRoute><PedidoDetalle /></ProtectedRoute>} />
        <Route path="/configuracion-apis" element={<ProtectedRoute><ConfiguracionAPIs /></ProtectedRoute>} />
        <Route path="/mapa-municipios" element={<ProtectedRoute><MapaComercial360 /></ProtectedRoute>} />
        <Route path="/admin/dedupe-gestoras" element={<ProtectedRoute superAdminOnly><DedupeGestoras /></ProtectedRoute>} />
        <Route path="/admin/ingesta-jobs" element={<ProtectedRoute adminOnly><IngestaJobs /></ProtectedRoute>} />
        <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
        <Route path="/correos" element={<ProtectedRoute permiso={{ recurso: 'correos', accion: 'leer' }}><Correos /></ProtectedRoute>} />
        <Route path="/correos/enviados" element={<ProtectedRoute permiso={{ recurso: 'correos', accion: 'leer' }}><CorreosEnviados /></ProtectedRoute>} />
        <Route path="/correos/ingesta" element={<ProtectedRoute permiso={{ recurso: 'correos', accion: 'leer' }}><CorreosIngesta /></ProtectedRoute>} />
        <Route path="/admin/ia-stats" element={<ProtectedRoute><IAStats /></ProtectedRoute>} />
        <Route path="/enriquecimiento" element={<ProtectedRoute><EnriquecimientoCorreo /></ProtectedRoute>} />
        <Route path="/noticias" element={<ProtectedRoute><NoticiasSector /></ProtectedRoute>} />
        <Route path="/sector" element={<ProtectedRoute><SectorAgua /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Root />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
